import { FramedAppMethods, FramedAppParentMethods } from '@wpp-open/core'
import { connectToChild, Methods } from 'penpal'
import { ComponentPropsWithoutRef, useEffect, useRef } from 'react'

import { useAppsContext } from 'providers/apps/AppsContext'

type Props = ComponentPropsWithoutRef<'iframe'> & {
  isFullscreen: boolean
  appId: string
  srcEmbeddedCode?: string
}

const EMBEDDED_CODE_RENDERER_LINK = 'https://public-assets.os.wpp.com/iframe/embedded-code-renderer.html'

export const PenpalParentFrame = ({ isFullscreen, appId, srcEmbeddedCode, ...iframeProps }: Props) => {
  const { getFullPageAppProps, getToolAppProps } = useAppsContext()
  const ref = useRef<HTMLIFrameElement>(null)

  // NOTE: Handle case for loading embeddedCode apps via predefined html
  // that accepts content and then document.write(content)
  useEffect(() => {
    if (!srcEmbeddedCode) return

    const iframe = ref.current!

    ref?.current?.contentWindow?.postMessage(srcEmbeddedCode, '*')
    const handlePassIframeContent = () => {
      iframe.contentWindow?.postMessage(srcEmbeddedCode, '*')
    }

    iframe.addEventListener('load', handlePassIframeContent)
    return () => {
      iframe.removeEventListener('load', handlePassIframeContent)
    }
  }, [srcEmbeddedCode])

  useEffect(() => {
    const { osApi, osContext } = isFullscreen
      ? getFullPageAppProps({ stableId: appId })
      : getToolAppProps({ stableId: appId })

    //  @ts-ignore
    const parentMethods: Methods = {
      osApi: { ...osApi },
    } satisfies FramedAppParentMethods

    const connection = connectToChild<FramedAppMethods>({
      iframe: ref.current!,
      methods: parentMethods,
      debug: false,
    })

    const unsubscribeFn = osContext.subscribe(osContext => {
      connection.promise.then(child => {
        child.receiveOsContext(osContext)
      })
    })

    connection.promise.catch(error => {
      console.error(`Penpal parent context error for app '${appId}':`, error)
    })

    return () => {
      unsubscribeFn()
      connection.destroy()
    }
  }, [appId, getToolAppProps, getFullPageAppProps, isFullscreen])

  return (
    <iframe
      ref={ref}
      data-testid="iframe"
      {...(srcEmbeddedCode ? { src: EMBEDDED_CODE_RENDERER_LINK } : {})}
      {...iframeProps}
    />
  )
}
